import { useState } from "react";
import { CleaningAutomationPreviewButton, CleaningNotificationHistory, useCleaningConfirmation } from "@/components/cleaning-message-confirmation";
import { useLocation, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { getWeeklyRotationDisplay, type WeeklyRotationPreview } from "@/lib/weekly-rotation-display";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { SearchableSelect } from "@/components/searchable-select";
import { searchIncludes, searchNormalize } from "@/lib/search-normalize";
import {
  ChevronLeft, ChevronRight, Loader2, Settings2, Sparkles, CalendarDays,
  AlertTriangle, CheckCircle2, X, MessageSquare, Link2, MapPin, ClipboardCheck, Inbox, SprayCan, Plus,
} from "lucide-react";
import type { CleaningProgram, CleaningAssignment, Property, ServiceFormTemplate } from "@shared/schema";
import { CleaningProofLightbox, type FormsReceivedItem } from "@/components/cleaning-proof-lightbox";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  CLEANING_TEMPLATE_KEYS,
  DEFAULT_CLEANING_NOTIFICATION_RULES,
  withCleaningNotificationDefaults,
  type CleaningNotificationRules,
  type CleaningTemplateKey,
} from "@shared/cleaning-notification-config";

// Member Cleaning System (Build 2) — operator surfaces:
//   • Portfolio timeline: properties as rows, days as columns, one block per
//     assigned member per day (weekly-rotation weeks render on their Monday).
//   • Per-property weekly calendar with popover drill-through (member, room,
//     jump-to-messaging, copy proof link) and a submission lightbox.
//   • Cleaning Forms Received queue + dashboard rollups (Phase 2).
// Status colors: green = proof submitted, red = past-due with no proof,
// neutral = upcoming, amber = uncovered.
import { Textarea } from "@/components/ui/textarea";

type EnrichedAssignment = CleaningAssignment & {
  memberName?: string | null;
  memberRoomLabel?: string | null;
  roomLabel?: string | null;
};

type CleanerVisit = {
  id: number;
  propertyId: number | null;
  date: string | null;
  title: string;
  status: string;
  vendorId: number | null;
  vendorName: string | null;
  priceCents: number | null;
};

type PortfolioResponse = {
  from: string;
  to: string;
  programs: CleaningProgram[];
  properties: Array<{ id: number; name: string | null; address: string | null }>;
  assignments: EnrichedAssignment[];
  cleanerVisits: CleanerVisit[];
};

type WeekResponse = {
  weekStart: string;
  weekEnd: string;
  program: CleaningProgram | null;
  assignments: EnrichedAssignment[];
  cleanerVisits: CleanerVisit[];
};

type GenerateResponse = {
  window: { from: string; to: string };
  participants: number[];
  created: number;
  removed: number;
  kept: number;
  uncoveredFlagged: number;
  distribution: number[];
  preview: boolean;
};

type DashboardResponse = {
  today: number;
  thisWeek: number;
  uncovered: number;
  awaitingProof: number;
  completionRate: number | null;
  windowFrom: string;
  memberCompletion: Array<{
    memberId: number;
    memberName: string | null;
    propertyId: number;
    propertyName: string;
    assigned: number;
    submitted: number;
    missed: number;
    completionRate: number | null;
    optedOut: boolean;
  }>;
};

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const TEMPLATE_LABELS: Record<CleaningTemplateKey, { label: string; help: string }> = {
  dayOfReminderTemplate: { label: "Day-of reminder", help: "Sent on the assigned day when proof has not already been submitted." },
  scheduleStartTemplate: { label: "Launch announcement", help: "Informational, one-time notice before the first assigned cleaning day. No early cleaning or proof link." },
  newMemberIntroTemplate: { label: "New-member introduction", help: "Explains the program and assignment when a member joins. Does not invite early cleaning or include a proof link." },
  followUpTemplate: { label: "Follow-up reminder", help: "Sent only while valid proof is missing, after the configured delay from the actual day-of send." },
  advanceReminderTemplate: { label: "Legacy advance template (not sent)", help: "Retained for compatibility only. Recurring advance reminders are disabled; the launch announcement above is used instead." },
};
function addDays(dateOnly: string, days: number): string {
  const t = Date.parse(`${dateOnly}T00:00:00Z`) + days * 24 * 60 * 60 * 1000;
  return new Date(t).toISOString().slice(0, 10);
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function dateOf(a: CleaningAssignment): string {
  return String(a.scheduledDate).slice(0, 10);
}

// Default status colors per the brief: green submitted, red past-due and not
// submitted, neutral upcoming; uncovered renders amber, skipped muted.
function statusClasses(a: EnrichedAssignment): string {
  if (a.status === "uncovered") return "bg-amber-100 text-amber-800 border-amber-200";
  if (a.status === "submitted" || a.status === "completed") return "bg-green-100 text-green-800 border-green-200";
  if (a.status === "skipped") return "bg-gray-100 text-gray-400 border-gray-200 line-through";
  if (dateOf(a) < todayStr()) return "bg-red-100 text-red-800 border-red-200";
  return "bg-brand-muted text-brand border-transparent";
}

function AssignmentBlock({ a, onClick }: { a: EnrichedAssignment; onClick?: () => void }) {
  const body = a.status === "uncovered" ? (
    <span className="inline-flex items-center gap-1">
      <AlertTriangle className="h-3 w-3 shrink-0" />
      Uncovered{a.roomLabel ? ` · Rm ${a.roomLabel}` : ""}
    </span>
  ) : (
    <>
      {(a.status === "submitted" || a.status === "completed") && <CheckCircle2 className="inline h-3 w-3 mr-0.5 -mt-0.5" />}
      {a.memberName ?? `Member ${a.assigneeMemberId}`}
      {a.period === "week" ? " · week" : ""}
    </>
  );
  return (
    <div
      className={`rounded border px-1.5 py-0.5 text-[11px] leading-tight truncate ${statusClasses(a)} ${onClick ? "cursor-pointer hover:opacity-80" : ""}`}
      title={`${a.memberName ?? "Uncovered"}${a.roomLabel ? ` · Room ${a.roomLabel}` : ""} · ${a.status}`}
      onClick={onClick}
      data-testid={`cleaning-block-${a.id}`}
    >
      {body}
    </div>
  );
}

// ── Week-view popover (member, room, jump-to-messaging, proof link) ─────────

function AssignmentPopover({ a, onViewForm }: { a: EnrichedAssignment; onViewForm: (assignmentId: number) => void }) {
  const confirmation = useCleaningConfirmation(a.propertyId);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const invalidateCalendars = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/app/cleaning"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/cleaning"] });
  };
  const overrideMutation = useMutation({
    mutationFn: async (body: { status: "skipped" | "completed" }) => {
      const res = await apiRequest("PATCH", `/api/app/cleaning/assignments/${a.id}`, body);
      return res.json();
    },
    onSuccess: (_data, body) => {
      invalidateCalendars();
      toast({ title: body.status === "skipped" ? "Slot skipped" : "Marked complete" });
    },
    onError: (e: Error) => toast({ title: "Update failed", description: parseErrorMessage(e, "Please try again."), variant: "destructive" }),
  });
  const linkMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/cleaning/assignments/${a.id}/form-link`, {});
      return res.json() as Promise<{ formUrl: string }>;
    },
    onSuccess: (data) => {
      navigator.clipboard.writeText(data.formUrl).catch(() => {});
      toast({ title: "Proof link copied", description: "Send it to the member — their submission completes this slot." });
    },
    onError: (e: Error) => toast({ title: "Couldn't create the link", description: parseErrorMessage(e, "Please try again."), variant: "destructive" }),
  });
  const done = a.status === "submitted" || a.status === "completed";
  return (
    <>
    <Popover>
      <PopoverTrigger asChild>
        <div>
          <AssignmentBlock a={a} onClick={() => {}} />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" data-testid={`popover-cleaning-${a.id}`}>
        <p className="text-sm font-medium text-gray-900">{a.memberName ?? "Uncovered slot"}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {a.memberRoomLabel || a.roomLabel ? `Room ${a.memberRoomLabel ?? a.roomLabel} · ` : ""}
          {dateOf(a)}{a.period === "week" ? " (whole week)" : ""} · {a.status}
        </p>
        <div className="mt-3 space-y-1.5">
          {a.assigneeMemberId != null && (
            <Button
              size="sm"
              variant="outline"
              className="w-full justify-start"
              asChild
              data-testid={`button-cleaning-message-${a.id}`}
            >
              <Link href={`/app/messages?member=${a.assigneeMemberId}&exact=1&cleaningProperty=${a.propertyId}&cleaningAssignment=${a.id}`}>
                <MessageSquare className="h-3.5 w-3.5 mr-2" />
                Message {a.memberName ?? "member"}
              </Link>
            </Button>
          )}
          {a.assigneeMemberId != null && <Button size="sm" variant="outline" disabled={confirmation.busy} onClick={() => confirmation.requestPreview({ action: "assignment", assignmentId: a.id })}>Preview message</Button>}
          {done && a.submissionId != null && (
            <Button size="sm" variant="outline" className="w-full justify-start" onClick={() => onViewForm(a.id)} data-testid={`button-cleaning-view-form-${a.id}`}>
              <ClipboardCheck className="h-3.5 w-3.5 mr-2" />
              View submitted form
            </Button>
          )}
          {!done && a.assigneeMemberId != null && (
            <Button size="sm" variant="outline" className="w-full justify-start" onClick={() => linkMutation.mutate()} disabled={linkMutation.isPending} data-testid={`button-cleaning-copy-link-${a.id}`}>
              {linkMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Link2 className="h-3.5 w-3.5 mr-2" />}
              Copy proof form link
            </Button>
          )}
          {!done && a.status !== "skipped" && (
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => overrideMutation.mutate({ status: "completed" })} disabled={overrideMutation.isPending} data-testid={`button-cleaning-complete-${a.id}`}>
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                Complete
              </Button>
              <Button size="sm" variant="outline" className="flex-1 text-gray-500" onClick={() => overrideMutation.mutate({ status: "skipped" })} disabled={overrideMutation.isPending} data-testid={`button-cleaning-skip-${a.id}`}>
                Skip
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
    {confirmation.dialog}
    </>
  );
}

// ── Third-party cleaner visit overlay band (Phase 4) ────────────────────────
// A cleaner visit is a real vendor task — the band deep-links to the task.

function CleanerVisitBand({ v, dateLabel }: { v: CleanerVisit; dateLabel?: string }) {
  const done = ["completed", "approved", "paid"].includes(v.status);
  return (
    <a
      href={`/app/tasks?task=${v.id}`}
      className={`block rounded border px-1.5 py-0.5 text-[11px] leading-tight truncate font-bold ${done ? "bg-violet-100 text-violet-500 border-violet-200" : "bg-violet-600 text-white border-violet-700"} hover:opacity-80`}
      title={`Cleaner visit — ${v.vendorName ?? "vendor"} · ${v.status}`}
      data-testid={`cleaner-visit-band-${v.id}`}
    >
      <SprayCan className="inline h-3 w-3 mr-0.5 -mt-0.5" />
      {dateLabel ? `${dateLabel} · ` : ""}{v.vendorName ?? "Cleaner"}
    </a>
  );
}

function mondayOfStr(dateOnly: string): string {
  const wd = new Date(`${dateOnly}T00:00:00Z`).getUTCDay(); // 0=Sun
  return addDays(dateOnly, -((wd + 6) % 7));
}

// Shared color key for both calendar surfaces.
function CleaningLegend() {
  const chips = [
    { cls: "bg-brand-muted border-transparent", label: "Scheduled" },
    { cls: "bg-green-100 border-green-200", label: "Proof submitted" },
    { cls: "bg-red-100 border-red-200", label: "Past due, no proof" },
    { cls: "bg-amber-100 border-amber-200", label: "Uncovered" },
    { cls: "bg-gray-100 border-gray-200", label: "Skipped" },
    { cls: "bg-violet-600 border-violet-700", label: "Cleaner visit" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500" data-testid="cleaning-legend">
      {chips.map((c) => (
        <span key={c.label} className="inline-flex items-center gap-1">
          <span className={`inline-block h-3 w-3 rounded border ${c.cls}`} />
          {c.label}
        </span>
      ))}
    </div>
  );
}

// ── Set Up / Edit Program picker ─────────────────────────────────────────────
// The single entry point for creating or editing a per-property program: a
// searchable property list (matches name + address) annotated with each
// property's current state, so "has a program but the add-on is off" is
// visible before the dialog even opens.

function SetupProgramPicker({ properties, programs, onPick, className, testId, dataTour, placeholder = "Set Up / Edit Program" }: {
  properties: Property[];
  programs: CleaningProgram[];
  onPick: (id: number) => void;
  className?: string;
  testId?: string;
  dataTour?: string;
  placeholder?: string;
}) {
  const byProperty = new Map(programs.map((p) => [p.propertyId, p]));
  return (
    <SearchableSelect
      value=""
      onValueChange={(v) => onPick(parseInt(v))}
      placeholder={placeholder}
      searchPlaceholder="Search by name or address…"
      emptyText="No properties found."
      className={className}
      leadingIcon={<Plus className="h-4 w-4 mr-1.5 shrink-0" />}
      testId={testId}
      triggerProps={dataTour ? { "data-tour": dataTour } : undefined}
      options={properties.map((p) => {
        const prog = byProperty.get(p.id);
        const label = p.name || p.address || `Property ${p.id}`;
        const active = !!prog?.addOnEnabled && prog?.isActive !== false;
        return {
          value: String(p.id),
          label,
          keywords: `${p.name ?? ""} ${p.address ?? ""}`,
          labelNode: (
            <span className="flex w-full items-center justify-between gap-2">
              <span className="truncate">{label}</span>
              {prog && (
                <Badge className={`shrink-0 border text-[10px] px-1.5 py-0 ${active ? "bg-green-100 text-green-800 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                  {active ? "Active" : "Add-on off"}
                </Badge>
              )}
            </span>
          ),
        };
      })}
    />
  );
}

// ── Standing-day editor (daily roster) ──────────────────────────────────────
// Members trade days ("I can't do Tuesdays"). Each change PUTs the standing
// map and regenerates the future horizon; a swap is just two changes. Manual
// choices survive regeneration — the distribution shape only guides where NEW
// members land.

function StandingDaysSection({ propertyId, program }: { propertyId: number; program: CleaningProgram }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");

  const membersQuery = useQuery<Array<{ id: number; name: string | null; email: string | null; status: string; currentPropertyId?: number | null }>>({
    queryKey: ["/api/app/devices/members"],
  });
  const participants = (membersQuery.data ?? []).filter(
    (m) => m.status === "active" && m.currentPropertyId === propertyId,
  );
  const standing = (program.standingDays ?? {}) as Record<string, number>;
  const enabledDays = (program.daysOfWeek as number[] | undefined) ?? [0, 1, 2, 3, 4, 5, 6];

  const moveMutation = useMutation({
    mutationFn: async ({ memberId, day }: { memberId: number; day: number }) => {
      const res = await apiRequest("PUT", `/api/app/properties/${propertyId}/cleaning/standing-days`, {
        days: { [String(memberId)]: day },
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "cleaning/program"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/cleaning"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      toast({ title: "Standing day updated", description: "Future days were regenerated; already-notified slots stay put." });
    },
    onError: (e: Error) => toast({ title: "Couldn't move the member", description: parseErrorMessage(e, "Please try again."), variant: "destructive" }),
  });

  if (participants.length === 0) return null;

  return (
    <div className="rounded-md border px-3 py-2 space-y-2">
      <div>
        <Label>Standing days</Label>
        <p className="text-xs text-gray-500 mt-0.5">
          Who cleans on which weekday. Change two members to swap their schedules — future slots regenerate, notified ones don't move.
        </p>
      </div>
      <div className="space-y-1.5" data-testid="list-standing-days">
        {participants.map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-2 text-xs">
            <span className="text-gray-800 truncate">{m.name || m.email || `Member ${m.id}`}</span>
            <Select
              value={standing[String(m.id)] !== undefined ? String(standing[String(m.id)]) : ""}
              onValueChange={(v) => moveMutation.mutate({ memberId: m.id, day: parseInt(v) })}
              disabled={readOnly || moveMutation.isPending}
            >
              <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`select-standing-day-${m.id}`}>
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                {enabledDays.map((d) => (
                  <SelectItem key={d} value={String(d)}>{["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][d]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Opt-out notes (note only — no payment; excludes from generation) ────────

type OptOutRow = {
  id: number;
  memberId: number;
  memberName: string | null;
  note: string | null;
  padsplitNotified: boolean;
  effectiveFrom: string;
  origin?: "host" | "member_portal" | "padsplit_link" | string;
  status?: "pending" | "active" | string;
};

function OptOutRowEditor({ optOut, readOnly, busy, onPatch }: {
  optOut: OptOutRow;
  readOnly: boolean;
  busy: boolean;
  onPatch: (id: number, body: Record<string, unknown>) => void;
}) {
  const [note, setNote] = useState(optOut.note ?? "");
  const [effectiveFrom, setEffectiveFrom] = useState(String(optOut.effectiveFrom).slice(0, 10));
  const originLabel = optOut.origin === "member_portal"
    ? "Member portal"
    : optOut.origin === "padsplit_link"
      ? "PadSplit link"
      : optOut.origin === "member"
        ? "Member request"
      : optOut.origin
        ? optOut.origin.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
        : "Host";

  return (
    <div className="space-y-2 border-b border-gray-100 pb-2" data-testid={`opt-out-row-${optOut.id}`}>
      <div className="flex items-center justify-between gap-2 text-xs">
        <div className="min-w-0">
          <span className="font-medium text-gray-800">{optOut.memberName ?? `Member ${optOut.memberId}`}</span>
          <div className="mt-1 flex flex-wrap gap-1">
            <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{originLabel}</Badge>
            <Badge
              variant={optOut.status === "pending" ? "secondary" : "outline"}
              className="h-5 px-1.5 text-[10px] capitalize"
            >
              {optOut.status === "pending" ? "Awaiting approval" : optOut.status ?? "active"}
            </Badge>
          </div>
          <p className="mt-1 text-[11px] text-gray-500">
            {optOut.status === "pending"
              ? `Requested ${String(optOut.effectiveFrom).slice(0, 10)} · Awaiting approval`
              : `Effective ${String(optOut.effectiveFrom).slice(0, 10)}`}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {optOut.status === "pending" && (
            <Button
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={() => onPatch(optOut.id, { status: "active" })}
              disabled={readOnly || busy}
              data-testid={`button-opt-out-approve-${optOut.id}`}
            >
              Approve
            </Button>
          )}
          <label className="flex items-center gap-1 text-[11px] text-gray-500 cursor-pointer">
            <Switch
              checked={optOut.padsplitNotified}
              onCheckedChange={(value) => onPatch(optOut.id, { padsplitNotified: value })}
              disabled={readOnly || busy}
              data-testid={`switch-opt-out-padsplit-${optOut.id}`}
            />
            PadSplit told
          </label>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-[11px] text-gray-500"
            onClick={() => onPatch(optOut.id, optOut.status === "pending" ? { status: "rejected" } : { end: true })}
            disabled={readOnly || busy}
            data-testid={`button-opt-out-end-${optOut.id}`}
          >
            {optOut.status === "pending" ? "Reject" : "End"}
          </Button>
        </div>
      </div>
      <div className="grid gap-1.5 sm:grid-cols-[1fr_145px_auto]">
        <Input
          className="h-8 text-xs"
          aria-label={`Note for ${optOut.memberName ?? "member"}`}
          placeholder="Note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          disabled={readOnly}
        />
        <Input
          className="h-8 text-xs"
          aria-label={`${optOut.status === "pending" ? "Requested" : "Effective"} date for ${optOut.memberName ?? "member"}`}
          type="date"
          value={effectiveFrom}
          onChange={(event) => setEffectiveFrom(event.target.value)}
          disabled={readOnly}
        />
        <Button
          size="sm"
          variant="outline"
          className="h-8"
          disabled={readOnly || busy || !effectiveFrom || (note === (optOut.note ?? "") && effectiveFrom === String(optOut.effectiveFrom).slice(0, 10))}
          onClick={() => onPatch(optOut.id, { note: note.trim() || null, effectiveFrom })}
          data-testid={`button-opt-out-save-${optOut.id}`}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

function OptOutSection({ propertyId }: { propertyId: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const [memberId, setMemberId] = useState<string>("");
  const [note, setNote] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(todayStr());

  const optOutsQuery = useQuery<OptOutRow[]>({
    queryKey: ["/api/app/properties", propertyId, "cleaning/opt-outs"],
  });
  const membersQuery = useQuery<Array<{ id: number; name: string | null; email: string | null; status: string; currentPropertyId?: number | null }>>({
    queryKey: ["/api/app/devices/members"],
  });
  const propertyMembers = (membersQuery.data ?? []).filter(
    (m) => m.status === "active" && m.currentPropertyId === propertyId,
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "cleaning/opt-outs"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/cleaning"] });
  };

  const addMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/properties/${propertyId}/cleaning/opt-out`, {
        memberId: parseInt(memberId),
        note: note.trim() || null,
        effectiveFrom,
      });
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      setMemberId("");
      setNote("");
      setEffectiveFrom(todayStr());
      toast({ title: "Opt-out recorded", description: "They'll be excluded on the next generation. Remember to tell PadSplit support." });
    },
    onError: (e: Error) => toast({ title: "Couldn't record the opt-out", description: parseErrorMessage(e, "Please try again."), variant: "destructive" }),
  });

  const patchMutation = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: Record<string, unknown> }) => {
      const res = await apiRequest("PATCH", `/api/app/cleaning/opt-outs/${id}`, body);
      return res.json();
    },
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast({ title: "Update failed", description: parseErrorMessage(e, "Please try again."), variant: "destructive" }),
  });

  const optOuts = optOutsQuery.data ?? [];

  return (
    <div className="rounded-md border px-3 py-2 space-y-2">
      <div>
        <Label>Opt-outs</Label>
        <p className="text-xs text-gray-500 mt-0.5">
          Active opt-outs are excluded from generation. Review member requests, then notify PadSplit support when needed.
        </p>
      </div>
      {optOuts.map((o) => (
        <OptOutRowEditor
          key={o.id}
          optOut={o}
          readOnly={readOnly}
          busy={patchMutation.isPending}
          onPatch={(id, body) => patchMutation.mutate({ id, body })}
        />
      ))}
      <div className="grid gap-1.5 sm:grid-cols-[1fr_1fr_145px_auto]">
        <Select value={memberId} onValueChange={setMemberId}>
          <SelectTrigger className="h-8 text-xs flex-1" data-testid="select-opt-out-member">
            <SelectValue placeholder="Opt a member out…" />
          </SelectTrigger>
          <SelectContent className="max-h-52 overflow-y-auto">
            {propertyMembers.map((m) => (
              <SelectItem key={m.id} value={String(m.id)}>{m.name || m.email || `Member ${m.id}`}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input className="h-8 text-xs flex-1" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="input-opt-out-note" />
        <Input className="h-8 text-xs" type="date" aria-label="Opt-out effective date" value={effectiveFrom} onChange={(event) => setEffectiveFrom(event.target.value)} data-testid="input-opt-out-effective-from" />
        <Button size="sm" className="h-8" onClick={() => addMutation.mutate()} disabled={readOnly || !memberId || !effectiveFrom || addMutation.isPending} data-testid="button-opt-out-add">
          {addMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}
        </Button>
      </div>
    </div>
  );
}

// ── Program setup + generate dialog ──────────────────────────────────────────

export function ProgramSetupDialog({ propertyId, propertyLabel, open, onOpenChange }: {
  propertyId: number;
  propertyLabel: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");

  const programQuery = useQuery<(CleaningProgram & { optOutWeeklyPriceIncreaseCents?: number | null }) | null>({
    queryKey: ["/api/app/properties", propertyId, "cleaning/program"],
    enabled: open,
  });
  const templatesQuery = useQuery<ServiceFormTemplate[]>({
    queryKey: ["/api/app/service-forms/templates"],
    enabled: open,
  });
  const rotationQuery = useQuery<WeeklyRotationResponse>({
    queryKey: ["/api/app/properties", propertyId, "cleaning/weekly-rotation/preview"],
    enabled: open && programQuery.data?.model === "weekly_rotation",
  });

  const program = programQuery.data ?? null;
  const [model, setModel] = useState<string | null>(null);
  const [addOnEnabled, setAddOnEnabled] = useState<boolean | null>(null);
  const [horizonDays, setHorizonDays] = useState<number | null>(null);
  const [leadTimeDays, setLeadTimeDays] = useState<number | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [daysOfWeek, setDaysOfWeek] = useState<number[] | null>(null);
  const [notificationRules, setNotificationRules] = useState<CleaningNotificationRules | null>(null);
  const [optOutWeeklyPriceIncrease, setOptOutWeeklyPriceIncrease] = useState<string | null>(null);
  const [preview, setPreview] = useState<GenerateResponse | null>(null);

  const effModel = model ?? program?.model ?? "daily_roster";
  const effAddOn = addOnEnabled ?? program?.addOnEnabled ?? false;
  const effHorizon = horizonDays ?? program?.horizonDays ?? 30;
  const effLead = leadTimeDays ?? program?.leadTimeDays ?? 7;
  const effTemplate = templateId ?? (program?.cleaningFormTemplateId ? String(program.cleaningFormTemplateId) : "none");
  const effDays = daysOfWeek ?? ((program?.daysOfWeek as number[] | undefined) ?? [0, 1, 2, 3, 4, 5, 6]);
  const effRules = withCleaningNotificationDefaults(notificationRules ?? (program?.rules as CleaningNotificationRules | undefined));
  const effOptOutWeeklyPriceIncrease = optOutWeeklyPriceIncrease
    ?? (((program?.optOutWeeklyPriceIncreaseCents ?? 0) / 100).toFixed(2));
  const optOutPriceValid = /^\d+(?:\.\d{1,2})?$/.test(effOptOutWeeklyPriceIncrease)
    && Number(effOptOutWeeklyPriceIncrease) >= 0
    && Number(effOptOutWeeklyPriceIncrease) <= 10_000;
  const optOutWeeklyPriceIncreaseCents = optOutPriceValid
    ? Math.round(Number(effOptOutWeeklyPriceIncrease) * 100)
    : null;
  const setRule = <K extends keyof CleaningNotificationRules>(key: K, value: CleaningNotificationRules[K]) =>
    setNotificationRules((current) => ({
      ...withCleaningNotificationDefaults(current ?? (program?.rules as CleaningNotificationRules | undefined)),
      [key]: value,
    }));

  const confirmation = useCleaningConfirmation(propertyId, () => {
    queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "cleaning/program"] });
    toast({ title: "Scheduled cleaning automation enabled", description: "No messages were sent immediately. See the saved notification schedule for timing." });
  });
  const saveMutation = useMutation({
    mutationFn: async () => {
      const settings = {
        model: effModel,
        addOnEnabled: effAddOn,
        horizonDays: effHorizon,
        leadTimeDays: effLead,
        daysOfWeek: effDays,
        cleaningFormTemplateId: effTemplate !== "none" ? parseInt(effTemplate) : null,
        optOutWeeklyPriceIncreaseCents,
        rules: {
          reminderHour: effRules.reminderHour,
          followUpDelayHours: effRules.followUpDelayHours,
          ...Object.fromEntries(CLEANING_TEMPLATE_KEYS.map((key) => [key, effRules[key]])),
        },
      };
      if (effAddOn && (!program?.addOnEnabled || !program?.isActive)) {
        await confirmation.requestPreview({ action: "activation", settings });
        return null;
      }
      const res = await apiRequest("PUT", `/api/app/properties/${propertyId}/cleaning/program`, settings);
      return res.json() as Promise<CleaningProgram>;
    },
    onSuccess: (data) => {
      if (!data) return;
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "cleaning/program"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/cleaning"] });
      toast({ title: "Cleaning program saved" });
    },
    onError: (e: Error) => toast({ title: "Failed to save program", description: parseErrorMessage(e, "Please try again."), variant: "destructive" }),
  });

  const generateMutation = useMutation({
    mutationFn: async ({ previewOnly }: { previewOnly: boolean }) => {
      const res = await apiRequest("POST", `/api/app/properties/${propertyId}/cleaning/generate?preview=${previewOnly}`, {});
      return res.json() as Promise<GenerateResponse>;
    },
    onSuccess: (data) => {
      if (data.preview) {
        setPreview(data);
      } else {
        setPreview(null);
        queryClient.invalidateQueries({ queryKey: ["/api/app/cleaning"] });
        queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "cleaning/calendar"] });
        queryClient.invalidateQueries({ queryKey: ["/api/app/cleaning"] });
        toast({ title: "Schedule generated", description: `${data.created} assignment(s) created, ${data.kept} kept.` });
      }
    },
    onError: (e: Error) => toast({ title: "Generation failed", description: parseErrorMessage(e, "Please try again."), variant: "destructive" }),
  });
  const rotationMutation = useMutation({
    mutationFn: async (roomId: string) => {
      const res = await apiRequest("PUT", `/api/app/properties/${propertyId}/cleaning/weekly-rotation`, { roomId: Number(roomId) });
      return res.json() as Promise<WeeklyRotationResponse>;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        ["/api/app/properties", propertyId, "cleaning/weekly-rotation/preview"],
        data,
      );
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "cleaning/program"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/cleaning"] });
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === "/api/app/properties"
          && Number(queryKey[1]) === propertyId
          && typeof queryKey[2] === "string"
          && queryKey[2].startsWith("cleaning/calendar"),
      });
      const lockedExceptions = data.rotations.filter((item) => item.differsFromRotation).length;
      toast({
        title: "Weekly rotation updated",
        description: lockedExceptions > 0
          ? `${lockedExceptions} locked week${lockedExceptions === 1 ? "" : "s"} stayed with the prior assignment and ${lockedExceptions === 1 ? "is" : "are"} marked below.`
          : "Unlocked future weeks were regenerated. Locked weeks stayed put.",
      });
    },
    onError: (e: Error) => toast({ title: "Couldn't update the rotation", description: parseErrorMessage(e, "Please try again."), variant: "destructive" }),
  });

  const busy = saveMutation.isPending || generateMutation.isPending || rotationMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (busy) return; onOpenChange(v); if (!v) setPreview(null); }}>
      <DialogContent
        className="max-w-lg max-h-[85vh] overflow-y-auto"
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Cleaning program — {propertyLabel}</DialogTitle>
        </DialogHeader>
        {programQuery.isLoading ? (
          <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
        ) : (
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <div>
                <Label htmlFor="cleaning-addon">Cleaning add-on enabled</Label>
                <p className="text-xs text-gray-500 mt-0.5">The module is inert for this property until enabled.</p>
              </div>
              <Switch id="cleaning-addon" checked={effAddOn} onCheckedChange={setAddOnEnabled} disabled={readOnly} data-testid="switch-cleaning-addon" />
            </div>

            <div>
              <Label>Model</Label>
              <Select value={effModel} onValueChange={setModel}>
                <SelectTrigger data-testid="select-cleaning-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily_roster">Daily Duty Roster — a member cleans the whole house each day</SelectItem>
                  <SelectItem value="weekly_rotation">Weekly Rotation — one member owns the house for a week, by room order</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {effModel === "daily_roster" && (
              <div>
                <Label>Covered days</Label>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {DAY_LABELS.map((label, day) => {
                    const active = effDays.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        disabled={readOnly}
                        onClick={() => setDaysOfWeek(active ? effDays.filter((d) => d !== day) : [...effDays, day].sort())}
                        className={`px-2 py-1 rounded text-xs font-medium border ${active ? "bg-brand text-white border-transparent" : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"}`}
                        data-testid={`button-cleaning-day-${day}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 items-end gap-3">
              <div>
                <Label>Horizon (days)</Label>
                <Input type="number" min={7} max={90} value={effHorizon} onChange={(e) => setHorizonDays(Number(e.target.value))} data-testid="input-cleaning-horizon" />
              </div>
              <div>
                <Label htmlFor="cleaning-lead">Launch notice (days before)</Label>
                <Input id="cleaning-lead" aria-describedby="cleaning-lead-help" type="number" min={0} max={60} value={effLead} onChange={(e) => setLeadTimeDays(Number(e.target.value))} disabled={readOnly} data-testid="input-cleaning-lead" />
              </div>
            </div>
            <p id="cleaning-lead-help" className="text-xs text-gray-500">Days before the first assigned cleaning day to send the launch announcement. Zero means no advance announcement; the day-of reminder still sends. Editing lead time never changes cleaning days or proof deadlines. Launch announcements are not repeated for recurring assignments or reactivation, and past launch windows are not replayed.</p>

            <div>
              <Label htmlFor="cleaning-opt-out-weekly-price">Opt-out weekly price increase</Label>
              <div className="relative mt-1">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-gray-500">$</span>
                <Input
                  id="cleaning-opt-out-weekly-price"
                  className="pl-7"
                  type="text"
                  inputMode="decimal"
                  value={effOptOutWeeklyPriceIncrease}
                  onChange={(event) => setOptOutWeeklyPriceIncrease(event.target.value)}
                  disabled={readOnly}
                  aria-invalid={!optOutPriceValid}
                  aria-describedby="cleaning-opt-out-weekly-price-help"
                  data-testid="input-cleaning-opt-out-weekly-price"
                />
              </div>
              <p
                id="cleaning-opt-out-weekly-price-help"
                className={`mt-1 text-xs ${optOutPriceValid ? "text-gray-500" : "text-red-600"}`}
              >
                {optOutPriceValid
                  ? "Added to a member's weekly housing price after their opt-out is approved."
                  : "Enter a dollar amount from $0.00 to $10,000.00 with no more than two decimal places."}
              </p>
            </div>

            <div className="rounded-md border bg-slate-50/70 p-3 space-y-3" data-testid="cleaning-notification-settings">
              <div>
                <Label className="text-sm font-semibold">Member notifications</Label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Timed in this property's timezone, then the account timezone. Variables: {"{{memberFirstName}}"}, {"{{memberName}}"}, {"{{propertyLabel}}"}, {"{{programStart}}"}, {"{{assignmentDescription}}"}, {"{{cleaningDay}}"}, {"{{weekStart}}"}, {"{{weekEnd}}"}, {"{{period}}"}, {"{{formLink}}"}. Proof links are for eligible day-of and follow-up messages only, never launch announcements or introductions.
                </p>
              </div>
              <p className="text-xs text-gray-500">Follow-ups are timed from the actual day-of send plus the delay, not the reminder hour, and are suppressed after valid proof.</p>
              <CleaningAutomationPreviewButton propertyId={propertyId} disabled={busy || !program} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cleaning-reminder-hour">Reminder hour</Label>
                  <Select value={String(effRules.reminderHour)} onValueChange={(value) => setRule("reminderHour", Number(value))} disabled={readOnly}>
                    <SelectTrigger id="cleaning-reminder-hour" data-testid="select-cleaning-reminder-hour"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, hour) => (
                        <SelectItem key={hour} value={String(hour)}>
                          {new Intl.DateTimeFormat("en-US", { hour: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(2024, 0, 1, hour)))}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="cleaning-follow-up-delay">Follow-up delay (hours)</Label>
                  <Input id="cleaning-follow-up-delay" type="number" min={1} max={720} value={effRules.followUpDelayHours} onChange={(e) => setRule("followUpDelayHours", Number(e.target.value))} disabled={readOnly} data-testid="input-cleaning-follow-up-delay" />
                </div>
              </div>
              <div className="space-y-3">
                {CLEANING_TEMPLATE_KEYS.map((key) => (
                  <div key={key}>
                    <div className="flex items-baseline justify-between gap-3">
                      <Label htmlFor={`cleaning-${key}`}>{TEMPLATE_LABELS[key].label}</Label>
                      <button type="button" className="text-[11px] text-brand hover:underline" onClick={() => setRule(key, DEFAULT_CLEANING_NOTIFICATION_RULES[key])} disabled={readOnly}>
                        Reset default
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-1">{TEMPLATE_LABELS[key].help}</p>
                    <Textarea id={`cleaning-${key}`} rows={3} maxLength={2000} value={effRules[key]} onChange={(e) => setRule(key, e.target.value)} disabled={readOnly} data-testid={`textarea-cleaning-${key}`} />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Proof-of-completion form</Label>
              <p className="text-xs text-gray-500 mb-1">Members submit this checklist + photo form to prove the clean.</p>
              <Select value={effTemplate} onValueChange={setTemplateId}>
                <SelectTrigger data-testid="select-cleaning-template">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No form yet</SelectItem>
                  {(templatesQuery.data ?? []).map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Link
                href="/app/operations/service-forms?templateType=cleaning"
                className="mt-1 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                data-testid="link-cleaning-form-templates"
              >
                Create or edit a cleaning form →
              </Link>
            </div>

            {program && effModel === "daily_roster" && (
              <StandingDaysSection propertyId={propertyId} program={program} />
            )}
            {program && effModel === "weekly_rotation" && (
              <div className="rounded-md border px-3 py-3 space-y-2.5" data-testid="weekly-rotation-settings">
                <div>
                  <Label>This week's room</Label>
                  <p className="text-xs text-gray-500 mt-0.5">Choose where the rotation points this week. Future unlocked weeks update automatically; locked weeks remain assigned as shown below.</p>
                </div>
                <Select
                  value={rotationQuery.data?.rotations[0] ? String(rotationQuery.data.rotations[0].room.id) : ""}
                  onValueChange={(roomId) => rotationMutation.mutate(roomId)}
                  disabled={readOnly || rotationMutation.isPending || rotationQuery.isLoading}
                >
                  <SelectTrigger data-testid="select-weekly-rotation-room">
                    <SelectValue placeholder={rotationQuery.isLoading ? "Loading rooms…" : "No bedrooms available"} />
                  </SelectTrigger>
                  <SelectContent>
                    {(rotationQuery.data?.rooms ?? []).map((room) => (
                      <SelectItem key={room.id} value={String(room.id)}>Room {room.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="border-t pt-2 space-y-1.5" data-testid="list-weekly-rotation-preview">
                  {(rotationQuery.data?.rotations ?? []).map((item, index) => {
                    const locked = item.lockedAssignment;
                    const displayed = getWeeklyRotationDisplay(item);
                    return (
                      <div key={item.weekStart} className={`rounded px-2 py-1.5 text-xs ${item.differsFromRotation ? "bg-amber-50 border border-amber-200" : ""}`}>
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-500 shrink-0">{index === 0 ? "This week" : index === 1 ? "Next week" : new Date(`${item.weekStart}T00:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                          <span className={displayed.uncovered ? "text-amber-700" : "text-gray-800"}>
                            {displayed.room ? `Room ${displayed.room.label} — ` : ""}
                            {displayed.uncovered ? (locked ? "(unassigned — locked)" : "(vacant — will be uncovered)") : displayed.member?.name ?? `Member ${displayed.member?.id}`}
                          </span>
                        </div>
                        {locked && (
                          <div className="mt-1 flex items-start justify-between gap-2 pl-16">
                            <Badge variant="outline" className="h-5 border-amber-300 bg-white px-1.5 text-[10px] capitalize text-amber-800">
                              {locked.status} · locked
                            </Badge>
                            {item.differsFromRotation && (
                              <span className="text-right text-[11px] text-amber-800">
                                Rotation plan: Room {item.room.label} — {item.uncovered ? "vacant" : item.member?.name ?? `Member ${item.member?.id}`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!rotationQuery.isLoading && !rotationQuery.data?.rotations.length && <p className="text-xs text-gray-500">Add bedrooms to this property to start a rotation.</p>}
                </div>
              </div>
            )}

            <OptOutSection propertyId={propertyId} />
            <CleaningNotificationHistory propertyId={propertyId} />
            {confirmation.dialog}

            <div className="flex gap-2">
              <Button className="flex-1" onClick={() => saveMutation.mutate()} disabled={readOnly || busy || confirmation.busy || !optOutPriceValid} data-testid="button-save-cleaning-program">
                {saveMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Preparing…</> : effAddOn && (!program?.addOnEnabled || !program?.isActive) ? "Preview activation" : "Save program"}
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => generateMutation.mutate({ previewOnly: true })}
                disabled={readOnly || busy || !program || !program.addOnEnabled}
                title={!program?.addOnEnabled ? "Save with the add-on enabled first" : undefined}
                data-testid="button-preview-cleaning"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Preview
              </Button>
            </div>

            {preview && (
              <div className="rounded-md border bg-gray-50 p-3 space-y-2" data-testid="panel-cleaning-preview">
                <p className="text-sm font-medium text-gray-800">
                  {preview.participants.length} participant(s) · {preview.created} assignment(s) would be created
                  {preview.kept > 0 ? `, ${preview.kept} kept` : ""}{preview.removed > 0 ? `, ${preview.removed} replaced` : ""}
                </p>
                {effModel === "daily_roster" && (
                  <div className="flex gap-1">
                    {preview.distribution.map((count, day) => (
                      <div key={day} className="flex-1 text-center">
                        <div className="text-[10px] text-gray-400">{DAY_LABELS[day]}</div>
                        <div className={`text-sm font-semibold rounded py-1 ${count === 0 ? "bg-gray-100 text-gray-300" : "bg-brand-muted text-brand"}`} data-testid={`preview-day-count-${day}`}>
                          {count}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <Button className="w-full" onClick={() => generateMutation.mutate({ previewOnly: false })} disabled={readOnly || busy} data-testid="button-generate-cleaning">
                  {generateMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating…</> : "Generate schedule"}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Schedule a third-party cleaner visit (a real vendor task) ───────────────

function ScheduleCleanerVisitDialog({ properties, open, onOpenChange }: {
  properties: Property[];
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const [propertyId, setPropertyId] = useState<string>("");
  const [vendorId, setVendorId] = useState<string>("");
  const [date, setDate] = useState<string>(todayStr());
  const [price, setPrice] = useState<string>("");
  const confirmation = useCleaningConfirmation(propertyId, () => {
    queryClient.invalidateQueries({ queryKey: ["/api/app/cleaning"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
    toast({ title: "Cleaner visit confirmed", description: "See notification history for delivery results." });
    onOpenChange(false);
    setVendorId("");
    setPrice("");
  });

  // Vendors are property-scoped: only vendors with team coverage on the chosen
  // property are offered, so a cleaner can't be booked onto a house they
  // don't work (the POST enforces the same rule server-side).
  const vendorsQuery = useQuery<Array<{ id: number; name: string }>>({
    queryKey: ["/api/app/properties", propertyId, "cleaning/eligible-vendors"],
    enabled: open && !!propertyId,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      await confirmation.requestPreview({ action: "visit", visit: {
        vendorId: parseInt(vendorId),
        date,
        priceCents: price.trim() ? Math.round(parseFloat(price) * 100) : null,
      } });
    },
    onSuccess: () => {
      // Preview only: keep the visit draft until explicit confirmation succeeds.
    },
    onError: (e: Error) => toast({ title: "Couldn't schedule the visit", description: parseErrorMessage(e, "Please try again."), variant: "destructive" }),
  });
  const busy = createMutation.isPending || confirmation.busy;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (busy) return; onOpenChange(v); }}>
      <DialogContent
        className="max-w-sm max-h-[85vh] overflow-y-auto"
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Schedule a one-off cleaner visit</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Property</Label>
            <Select value={propertyId} onValueChange={(v) => { setPropertyId(v); setVendorId(""); }}>
              <SelectTrigger data-testid="select-visit-property">
                <SelectValue placeholder="Select a property" />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                {properties.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>{p.name || p.address || `Property ${p.id}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Vendor</Label>
            <Select value={vendorId} onValueChange={setVendorId} disabled={!propertyId}>
              <SelectTrigger data-testid="select-visit-vendor">
                <SelectValue placeholder={!propertyId ? "Pick a property first" : "Select a vendor"} />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                {(vendorsQuery.data ?? []).map((v) => (
                  <SelectItem key={v.id} value={String(v.id)}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {propertyId && !vendorsQuery.isLoading && (vendorsQuery.data ?? []).length === 0 && (
              <p className="text-xs text-amber-600 mt-1" data-testid="text-no-eligible-vendors">
                No vendors are assigned to this property yet — assign a team under Operations → Vendors first.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="input-visit-date" />
            </div>
            <div>
              <Label>Price (optional)</Label>
              <Input type="number" min={0} step="0.01" placeholder="e.g. 120" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="input-visit-price" />
            </div>
          </div>
          <Button className="w-full" onClick={() => createMutation.mutate()} disabled={readOnly || !propertyId || !vendorId || !date || busy} data-testid="button-schedule-visit">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Preparing…</> : <><SprayCan className="h-4 w-4 mr-2" />Preview visit notifications</>}
          </Button>
          {confirmation.dialog}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function AppCleaning() {
  usePageTitle("Cleaning Program");
  const [setupFor, setSetupFor] = useState<{ id: number; label: string } | null>(null);
  const [weekPropertyId, setWeekPropertyId] = useState<string>("");
  const [weekStart, setWeekStart] = useState<string>(todayStr());
  // Deep links: /app/cleaning?tab=received&assignment=N (from the member-thread
  // proof card) lands on the queue with that assignment's lightbox open.
  const initialParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const [activeTab, setActiveTab] = useState<string>(initialParams.get("tab") === "received" ? "received" : "portfolio");
  const [visitDialogOpen, setVisitDialogOpen] = useState(false);
  const [lightboxAssignmentId, setLightboxAssignmentId] = useState<number | null>(() => {
    const raw = initialParams.get("assignment");
    return raw && /^\d+$/.test(raw) ? parseInt(raw, 10) : null;
  });

  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });
  const properties = (propertiesQuery.data ?? []).filter((p) => p.status !== "archive");
  const propertyLabel = (id: number) => {
    const p = properties.find((x) => x.id === id);
    return p?.name || p?.address || `Property ${id}`;
  };

  // Portfolio range — defaults to the next 30 days; fully settable, including
  // ranges entirely in the past (reviewing what was done). Server caps 92 days.
  const [rangeFrom, setRangeFrom] = useState<string>(todayStr());
  const [rangeTo, setRangeTo] = useState<string>(addDays(todayStr(), 29));
  const [gridSearch, setGridSearch] = useState("");
  const [memberSort, setMemberSort] = useState<"rate" | "missed" | "name">("rate");
  const from = rangeFrom;
  const portfolioQuery = useQuery<PortfolioResponse>({
    queryKey: ["/api/app/cleaning", `portfolio-calendar?from=${rangeFrom}&to=${rangeTo}`],
  });
  const dashboardQuery = useQuery<DashboardResponse>({
    queryKey: ["/api/app/cleaning", "dashboard"],
  });
  const formsReceivedQuery = useQuery<{ items: FormsReceivedItem[] }>({
    queryKey: ["/api/app/cleaning", "forms-received"],
  });

  // Calendar tab — a stacked 12-week window anchored at weekStart's Monday.
  const weekQuery = useQuery<WeekResponse>({
    queryKey: ["/api/app/properties", weekPropertyId, `cleaning/calendar?week=${weekStart}&weeks=12`],
    enabled: !!weekPropertyId,
  });

  const portfolio = portfolioQuery.data;
  const rangeSpan = Math.max(1, Math.min(93, Math.round((Date.parse(`${portfolio?.to ?? rangeTo}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / (24 * 60 * 60 * 1000)) + 1));
  const days = Array.from({ length: rangeSpan }, (_, i) => addDays(from, i));
  const assignmentsByPropertyDay = new Map<string, EnrichedAssignment[]>();
  for (const a of portfolio?.assignments ?? []) {
    const key = `${a.propertyId}:${dateOf(a)}`;
    const list = assignmentsByPropertyDay.get(key) ?? [];
    list.push(a);
    assignmentsByPropertyDay.set(key, list);
  }
  const programProperties = (portfolio?.programs ?? []).map((p) => p.propertyId);
  const programByProperty = new Map((portfolio?.programs ?? []).map((p) => [p.propertyId, p]));
  const gridNeedle = searchNormalize(gridSearch.trim());
  const visibleProgramProperties = gridNeedle
    ? programProperties.filter((pid) => {
        const p = properties.find((x) => x.id === pid);
        return searchIncludes(`${p?.name ?? ""} ${p?.address ?? ""}`, gridNeedle);
      })
    : programProperties;
  const visitsByPropertyDay = new Map<string, CleanerVisit[]>();
  for (const v of portfolio?.cleanerVisits ?? []) {
    if (!v.propertyId || !v.date) continue;
    const key = `${v.propertyId}:${v.date}`;
    const list = visitsByPropertyDay.get(key) ?? [];
    list.push(v);
    visitsByPropertyDay.set(key, list);
  }

  const week = weekQuery.data;
  const stackedWeekStarts = week ? Array.from({ length: 12 }, (_, i) => addDays(week.weekStart, i * 7)) : [];

  const receivedItems = formsReceivedQuery.data?.items ?? [];
  const lightboxItem = lightboxAssignmentId != null
    ? receivedItems.find((i) => i.assignment.id === lightboxAssignmentId) ?? null
    : null;
  const dash = dashboardQuery.data;
  const memberCompletion = [...(dash?.memberCompletion ?? [])].sort((a, b) => {
    if (memberSort === "name") return (a.memberName ?? `Member ${a.memberId}`).localeCompare(b.memberName ?? `Member ${b.memberId}`);
    if (memberSort === "missed") return b.missed - a.missed || (a.completionRate ?? Infinity) - (b.completionRate ?? Infinity);
    return (a.completionRate ?? Infinity) - (b.completionRate ?? Infinity) || b.missed - a.missed;
  });

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="page-cleaning">
        <ViewOnlyBanner area="maintenance" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" data-testid="text-page-title" data-tour="cleaning-heading">Cleaning Program</h1>
            <p className="text-sm text-gray-500 mt-1">Member self-cleaning rosters and rotations, generated from live occupancy</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <SetupProgramPicker
              properties={properties}
              programs={portfolio?.programs ?? []}
              onPick={(id) => setSetupFor({ id, label: propertyLabel(id) })}
              className="h-9 w-auto border-transparent bg-green-600 px-3 text-sm font-medium text-white hover:bg-green-700 [&>span]:text-white"
              testId="button-cleaning-setup-program"
              dataTour="cleaning-setup"
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" className="bg-brand hover:bg-brand-hover text-white" onClick={() => setVisitDialogOpen(true)} data-testid="button-open-visit-dialog" data-tour="cleaning-visit">
                  <SprayCan className="h-4 w-4 mr-1.5" />
                  Schedule One-Off Cleaner Visit
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                Book an as-needed professional cleaning visit with one of your eligible vendors.
                It's created as a normal vendor task — dispatch, chat, and payment work like any other job.
              </TooltipContent>
            </Tooltip>
            <ExplainerVideoButton pageKey="cleaning" />
            <GuideButton pageKey="cleaning" />
          </div>
          <GuideNudgeBanner pageKey="cleaning" />
        </div>

        {dash && (
          <div className="mb-4 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="strip-cleaning-dashboard" data-tour="cleaning-dashboard">
              {[
                { label: "Today", value: dash.today },
                { label: "This week", value: dash.thisWeek },
                { label: "Uncovered", value: dash.uncovered, alert: dash.uncovered > 0 },
                { label: "Awaiting proof", value: dash.awaitingProof },
                { label: "Completion (30d)", value: dash.completionRate != null ? `${dash.completionRate}%` : "—" },
              ].map((s) => (
                <Card key={s.label}>
                  <CardContent className="p-3 text-center">
                    <p className={`text-lg font-semibold ${"alert" in s && s.alert ? "text-amber-600" : "text-gray-900"}`}>{s.value}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Card data-testid="card-cleaning-member-completion">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">By member</h2>
                    <p className="text-xs text-gray-500">Trailing 30 days · assigned vs. submitted proof</p>
                  </div>
                  <Select value={memberSort} onValueChange={(value) => setMemberSort(value as "rate" | "missed" | "name")}>
                    <SelectTrigger className="h-8 w-[155px] text-xs" data-testid="select-member-completion-sort">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rate">Lowest rate first</SelectItem>
                      <SelectItem value="missed">Most missed first</SelectItem>
                      <SelectItem value="name">Member name</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {memberCompletion.length === 0 ? (
                  <p className="py-3 text-center text-sm text-gray-500">No member assignments were due in this window.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[620px] text-sm" data-testid="table-cleaning-member-completion">
                      <thead className="border-b text-left text-xs text-gray-500">
                        <tr>
                          <th className="pb-2 pr-4 font-medium">Member</th>
                          <th className="pb-2 pr-4 font-medium">Property</th>
                          <th className="pb-2 pr-4 text-right font-medium">Rate</th>
                          <th className="pb-2 pr-4 text-right font-medium">Assigned</th>
                          <th className="pb-2 pr-4 text-right font-medium">Submitted</th>
                          <th className="pb-2 text-right font-medium">Missed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {memberCompletion.map((row) => (
                          <tr key={`${row.memberId}:${row.propertyId}`} className="border-b last:border-0" data-testid={`member-completion-row-${row.memberId}-${row.propertyId}`}>
                            <td className="py-2.5 pr-4 font-medium text-gray-900">
                              {row.memberName ?? `Member ${row.memberId}`}
                              {row.optedOut && <Badge variant="outline" className="ml-2 border-amber-200 bg-amber-50 text-amber-700">Opted out</Badge>}
                            </td>
                            <td className="py-2.5 pr-4 text-gray-600">{row.propertyName}</td>
                            <td className="py-2.5 pr-4 text-right font-semibold text-gray-900">{row.optedOut ? "—" : row.completionRate != null ? `${row.completionRate}%` : "—"}</td>
                            <td className="py-2.5 pr-4 text-right text-gray-700">{row.assigned}</td>
                            <td className="py-2.5 pr-4 text-right text-gray-700">{row.submitted}</td>
                            <td className={`py-2.5 text-right font-medium ${row.missed > 0 ? "text-red-700" : "text-gray-700"}`}>{row.missed}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="portfolio" data-testid="tab-cleaning-portfolio">Portfolio</TabsTrigger>
            <TabsTrigger value="property" data-testid="tab-cleaning-property" data-tour="cleaning-week">Calendar</TabsTrigger>
            <TabsTrigger value="received" data-testid="tab-cleaning-received">
              <Inbox className="h-3.5 w-3.5 mr-1" />
              Forms received{receivedItems.length > 0 ? ` (${receivedItems.length})` : ""}
            </TabsTrigger>
          </TabsList>

          {/* ── Cross-property portfolio timeline ── */}
          <TabsContent value="portfolio" className="mt-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Input
                placeholder="Search properties…"
                value={gridSearch}
                onChange={(e) => setGridSearch(e.target.value)}
                className="h-8 w-[200px] text-xs"
                data-testid="input-cleaning-search"
                data-tour="cleaning-search"
              />
              <Label className="text-xs text-gray-500">From</Label>
              <Input
                type="date"
                value={rangeFrom}
                onChange={(e) => { if (e.target.value) { setRangeFrom(e.target.value); if (rangeTo < e.target.value) setRangeTo(addDays(e.target.value, 29)); } }}
                className="h-8 w-[150px] text-xs"
                data-testid="input-portfolio-from"
              />
              <Label className="text-xs text-gray-500">To</Label>
              <Input
                type="date"
                value={rangeTo}
                min={rangeFrom}
                onChange={(e) => { if (e.target.value && e.target.value >= rangeFrom) setRangeTo(e.target.value); }}
                className="h-8 w-[150px] text-xs"
                data-testid="input-portfolio-to"
              />
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => { setRangeFrom(todayStr()); setRangeTo(addDays(todayStr(), 29)); }}
                data-testid="button-portfolio-range-reset"
              >
                Next 30 days
              </Button>
              <p className="text-[11px] text-gray-400">Scroll the grid sideways for the full range.</p>
            </div>
            {portfolioQuery.isLoading ? (
              <p className="text-sm text-gray-400 text-center py-10">Loading…</p>
            ) : programProperties.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center" data-tour="cleaning-portfolio">
                  <CalendarDays className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No cleaning programs yet</p>
                  <p className="text-sm text-gray-400 mt-1 mb-4">Pick a property below to set up a Daily Duty Roster or Weekly Rotation.</p>
                  <div className="max-w-xs mx-auto">
                    <SetupProgramPicker
                      properties={properties}
                      programs={portfolio?.programs ?? []}
                      onPick={(id) => setSetupFor({ id, label: propertyLabel(id) })}
                      className="border-transparent bg-green-600 font-medium text-white hover:bg-green-700 [&>span]:text-white"
                      testId="select-setup-property"
                      placeholder="Set Up Cleaning Program"
                    />
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="overflow-x-auto border rounded-lg" data-tour="cleaning-portfolio" data-testid="grid-cleaning-portfolio">
                <table className="min-w-[900px] w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left px-3 py-2 font-medium text-gray-600 w-48">Property</th>
                      {days.map((d) => (
                        <th key={d} className={`px-1 py-2 font-medium text-center text-[11px] ${d === from ? "text-brand" : "text-gray-500"}`}>
                          {DAY_LABELS[new Date(`${d}T00:00:00Z`).getUTCDay()]}
                          <div className="text-[10px] font-normal">{d.slice(5)}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleProgramProperties.length === 0 && (
                      <tr>
                        <td colSpan={days.length + 1} className="px-3 py-6 text-center text-sm text-gray-400" data-testid="row-cleaning-no-match">
                          No properties match “{gridSearch}”.
                        </td>
                      </tr>
                    )}
                    {visibleProgramProperties.map((pid) => (
                      <tr key={pid} className="border-b last:border-b-0 align-top">
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            className="text-xs font-medium text-brand hover:underline break-words text-left"
                            onClick={() => { setWeekPropertyId(String(pid)); setActiveTab("property"); }}
                            title="Open this property's calendar"
                            data-testid={`link-cleaning-property-${pid}`}
                          >
                            {propertyLabel(pid)}
                          </button>
                          <div className="mt-1 flex items-center gap-1.5">
                            {(() => {
                              const prog = programByProperty.get(pid);
                              const active = !!prog?.addOnEnabled && prog?.isActive !== false;
                              return (
                                <Badge className={`border text-[10px] px-1.5 py-0 ${active ? "bg-green-100 text-green-800 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid={`badge-cleaning-status-${pid}`}>
                                  {active ? "Active" : "Add-on off"}
                                </Badge>
                              );
                            })()}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-[11px]"
                              onClick={() => setSetupFor({ id: pid, label: propertyLabel(pid) })}
                              data-testid={`button-cleaning-setup-${pid}`}
                            >
                              <Settings2 className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                          </div>
                        </td>
                        {(() => {
                          // Weekly-rotation blocks span their entire week as
                          // ONE long bubble (colSpan over the visible days of
                          // that week); daily items keep per-day cells.
                          const cells = [];
                          let i = 0;
                          while (i < days.length) {
                            const d = days[i];
                            const ws = mondayOfStr(d);
                            const weekly = (assignmentsByPropertyDay.get(`${pid}:${ws}`) ?? []).filter((a) => a.period === "week");
                            if (weekly.length > 0) {
                              const weekEnd = addDays(ws, 6);
                              let span = 0;
                              while (i + span < days.length && days[i + span] <= weekEnd) span++;
                              const spanDays = days.slice(i, i + span);
                              const spanVisits = spanDays.flatMap((sd) =>
                                (visitsByPropertyDay.get(`${pid}:${sd}`) ?? []).map((v) => ({ v, sd })));
                              const spanDaily = spanDays.flatMap((sd) =>
                                (assignmentsByPropertyDay.get(`${pid}:${sd}`) ?? []).filter((a) => a.period !== "week"));
                              cells.push(
                                <td key={d} colSpan={span} className="px-0.5 py-1 min-w-[62px]">
                                  <div className="space-y-0.5">
                                    {weekly.map((a) => (
                                      <AssignmentPopover key={a.id} a={a} onViewForm={setLightboxAssignmentId} />
                                    ))}
                                    {spanVisits.map(({ v, sd }) => <CleanerVisitBand key={`v-${v.id}`} v={v} dateLabel={sd.slice(5)} />)}
                                    {spanDaily.map((a) => (
                                      <AssignmentPopover key={a.id} a={a} onViewForm={setLightboxAssignmentId} />
                                    ))}
                                  </div>
                                </td>,
                              );
                              i += span;
                            } else {
                              const items = (assignmentsByPropertyDay.get(`${pid}:${d}`) ?? []).filter((a) => a.period !== "week");
                              const visits = visitsByPropertyDay.get(`${pid}:${d}`) ?? [];
                              cells.push(
                                <td key={d} className="px-0.5 py-1 min-w-[62px]">
                                  <div className="space-y-0.5">
                                    {visits.map((v) => <CleanerVisitBand key={`v-${v.id}`} v={v} />)}
                                    {items.map((a) => (
                                      <AssignmentPopover key={a.id} a={a} onViewForm={setLightboxAssignmentId} />
                                    ))}
                                  </div>
                                </td>,
                              );
                              i++;
                            }
                          }
                          return cells;
                        })()}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {programProperties.length > 0 && (
              <div className="mt-3">
                <CleaningLegend />
              </div>
            )}
          </TabsContent>

          {/* ── Per-property weekly view ── */}
          <TabsContent value="property" className="mt-4">
            <div className="flex flex-wrap items-center gap-2 mb-4" data-tour="cleaning-program-settings">
              <Select value={weekPropertyId} onValueChange={setWeekPropertyId}>
                <SelectTrigger className="w-[240px]" data-testid="select-week-property">
                  <SelectValue placeholder="Select a property" />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name || p.address || `Property ${p.id}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => setWeekStart(addDays(week?.weekStart ?? weekStart, -7))} disabled={!weekPropertyId} data-testid="button-week-prev">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setWeekStart(todayStr())} disabled={!weekPropertyId} data-testid="button-week-today">
                  Today
                </Button>
                <Button variant="outline" size="sm" onClick={() => setWeekStart(addDays(week?.weekStart ?? weekStart, 7))} disabled={!weekPropertyId} data-testid="button-week-next">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              {weekPropertyId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSetupFor({ id: parseInt(weekPropertyId), label: propertyLabel(parseInt(weekPropertyId)) })}
                  data-testid="button-week-program-settings"
                >
                  <Settings2 className="h-4 w-4 mr-1" />
                  Program
                </Button>
              )}
              {week?.program && (
                <Badge variant="secondary">
                  {week.program.model === "weekly_rotation" ? "Weekly Rotation" : "Daily Duty Roster"}
                  {!week.program.addOnEnabled ? " · add-on off" : ""}
                </Badge>
              )}
            </div>

            {!weekPropertyId ? (
              <p className="text-sm text-gray-400 text-center py-10">Pick a property to see its calendar.</p>
            ) : weekQuery.isLoading ? (
              <p className="text-sm text-gray-400 text-center py-10">Loading…</p>
            ) : (
              <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1" data-testid="grid-cleaning-week">
                <CleaningLegend />
                {stackedWeekStarts.map((ws) => {
                  const weeklyBands = (week?.assignments ?? []).filter((a) => a.period === "week" && dateOf(a) === ws);
                  return (
                  <div key={ws} className="border rounded-lg p-2 bg-white">
                    <div className="text-xs font-medium text-gray-600 mb-1.5" data-testid={`week-label-${ws}`}>
                      Week of {ws}
                    </div>
                    {weeklyBands.length > 0 && (
                      <div className="mb-1 space-y-0.5" data-testid={`week-band-${ws}`}>
                        {weeklyBands.map((a) => (
                          <AssignmentPopover key={a.id} a={a} onViewForm={setLightboxAssignmentId} />
                        ))}
                      </div>
                    )}
                    <div className="grid grid-cols-7 gap-1">
                      {Array.from({ length: 7 }, (_, i) => addDays(ws, i)).map((d) => {
                        const items = (week?.assignments ?? []).filter(
                          (a) => a.period !== "week" && dateOf(a) === d,
                        );
                        const dayVisits = (week?.cleanerVisits ?? []).filter((v) => v.date === d);
                        const isToday = d === todayStr();
                        return (
                          <div key={d} className={`rounded-md border min-h-[92px] p-1.5 ${isToday ? "border-brand bg-brand-muted/30" : "border-gray-100 bg-gray-50/50"}`}>
                            <div className={`text-[11px] font-medium mb-1 ${isToday ? "text-brand" : "text-gray-500"}`}>
                              {DAY_LABELS[new Date(`${d}T00:00:00Z`).getUTCDay()]} {d.slice(5)}
                            </div>
                            <div className="space-y-1">
                              {dayVisits.map((v) => <CleanerVisitBand key={`v-${v.id}`} v={v} />)}
                              {items.map((a) => (
                                <AssignmentPopover key={`${a.id}-${d}`} a={a} onViewForm={setLightboxAssignmentId} />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ── Cleaning Forms Received queue ── */}
          <TabsContent value="received" className="mt-4">
            <div data-tour="cleaning-received">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <p className="text-xs text-gray-500">Members' submitted proof forms land here.</p>
              <Link
                href="/app/operations/service-forms?templateType=cleaning"
                className="text-xs text-brand hover:underline"
                data-tour="cleaning-form-link"
                data-testid="link-cleaning-form-received"
              >
                Create or edit a cleaning form →
              </Link>
            </div>
            {formsReceivedQuery.isLoading ? (
              <p className="text-sm text-gray-400 text-center py-10">Loading…</p>
            ) : receivedItems.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Inbox className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No cleaning forms received yet</p>
                  <p className="text-sm text-gray-400 mt-1">Members' submitted proof forms from the last 30 days land here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="list-cleaning-forms-received">
                {receivedItems.map((item) => (
                  <Card key={item.assignment.id} data-testid={`received-row-${item.assignment.id}`}>
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                            {item.memberName ?? "Member"} · {item.propertyLabel ?? `Property ${item.assignment.propertyId}`}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {dateOf(item.assignment)}{item.assignment.period === "week" ? " (week)" : ""}
                            {item.submission?.submittedAt ? ` · submitted ${new Date(item.submission.submittedAt).toLocaleString()}` : ""}
                            {item.submission?.locationFlagged ? " · ⚠ location flagged" : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {item.assignment.assigneeMemberId != null && (
                            <Button size="sm" variant="ghost" asChild data-testid={`button-received-thread-${item.assignment.id}`}>
                              <a href={`/app/messages?member=${item.assignment.assigneeMemberId}&exact=1&cleaningProperty=${item.assignment.propertyId}&cleaningAssignment=${item.assignment.id}`}>
                                <MessageSquare className="h-3.5 w-3.5 mr-1" />
                                Thread
                              </a>
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => setLightboxAssignmentId(item.assignment.id)} data-testid={`button-received-view-${item.assignment.id}`}>
                            View form
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            </div>
          </TabsContent>
        </Tabs>

        <CleaningProofLightbox
          item={lightboxItem}
          open={lightboxItem != null}
          onOpenChange={(v) => { if (!v) setLightboxAssignmentId(null); }}
        />

        <ScheduleCleanerVisitDialog
          properties={properties}
          open={visitDialogOpen}
          onOpenChange={setVisitDialogOpen}
        />

        {setupFor && (
          <ProgramSetupDialog
            propertyId={setupFor.id}
            propertyLabel={setupFor.label}
            open={!!setupFor}
            onOpenChange={(v) => { if (!v) setSetupFor(null); }}
          />
        )}
      </div>
    </AppLayout>
  );
}

type WeeklyRotationResponse = {
  rooms: Array<{ id: number; label: string }>;
  rotations: WeeklyRotationPreview[];
};
